import type { ConfirmationResult, RecaptchaVerifier, User } from 'firebase/auth';
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { webApp } from '../data/site';
import {
  fieldValue,
  firebaseMessage,
  loadAuth,
  postJson,
  RequestTimeoutError,
  signOutPhone,
} from './accountForms';
import { track } from './analytics';
import { firebaseApp } from './firebase';
import { shareApiBase } from './share/shareContract';

/**
 * Forgot password. The person names their account and the mobile number on it; the server
 * confirms the two belong together (`/api/public/password-reset/start`, which says nothing about
 * a username on its own), Firebase sends the OTP to that number, and the code plus the new
 * password go back together (`/complete`). Every "Forgot password?" link in the apps opens this.
 *
 * Four steps on one form: who you are, the code, the new password, then the answer.
 */

const START_PATH = '/public/password-reset/start';
const COMPLETE_PATH = '/public/password-reset/complete';
const START_TIMEOUT_MS = 20_000;
const COMPLETE_TIMEOUT_MS = 30_000;
const MIN_PASSWORD_LENGTH = 8;

type Step = 'details' | 'code' | 'password' | 'done';
type Status = { text: string; state: 'success' | 'error' | '' };
type ServerError = { error?: string; code?: string };

export function PasswordResetForm() {
  const form = useRef<HTMLFormElement>(null);
  const verifier = useRef<RecaptchaVerifier | null>(null);
  const confirmation = useRef<ConfirmationResult | null>(null);
  const verifiedUser = useRef<User | null>(null);
  const started = useRef(false);
  const [step, setStep] = useState<Step>('details');
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<Status>({ text: '', state: '' });
  const [username, setUsername] = useState('');

  const fail = (text: string) => {
    setStatus({ text, state: 'error' });
    track('password_reset_error', { step });
  };

  async function checkAccountAndSendCode() {
    const element = form.current;
    if (!element || !element.reportValidity()) return;
    const app = firebaseApp();
    if (!app || !shareApiBase()) {
      fail('Password reset is not available on this site yet. Please ask your administrator.');
      return;
    }
    const typedUsername = fieldValue(element, 'username').trim();
    const mobile = fieldValue(element, 'mobile').replace(/\D/g, '');
    setBusy(true);
    setStatus({ text: 'Checking your account…', state: '' });
    try {
      const response = await postJson(
        `${shareApiBase()}${START_PATH}`,
        { username: typedUsername, phone: `+91${mobile}` },
        START_TIMEOUT_MS,
      );
      const body = (await response.json().catch(() => ({}))) as ServerError;
      if (!response.ok) {
        throw new Error(body.error || 'We could not find that account. Check the details.');
      }
      setStatus({ text: 'Sending a code to your phone…', state: '' });
      const { getAuth, RecaptchaVerifier, signInWithPhoneNumber } = await loadAuth();
      const auth = getAuth(app);
      verifier.current ??= new RecaptchaVerifier(auth, 'reset-recaptcha', { size: 'invisible' });
      confirmation.current = await signInWithPhoneNumber(auth, `+91${mobile}`, verifier.current);
      setStep('code');
      setStatus({ text: `We sent a 6-digit code to +91 ${mobile}.`, state: '' });
    } catch (error) {
      verifier.current?.clear();
      verifier.current = null;
      if (error instanceof RequestTimeoutError) {
        fail('The server took too long to answer. Check your connection and try again.');
      } else if ((error as { code?: string })?.code) {
        fail(firebaseMessage(error, 'We could not send a code. Check the number and try again.'));
      } else {
        fail(error instanceof Error ? error.message : 'We could not check that account.');
      }
    } finally {
      setBusy(false);
    }
  }

  async function verifyCode() {
    const code = fieldValue(form.current!, 'code').trim();
    if (!/^\d{6}$/.test(code)) {
      fail('Enter the 6-digit code from the SMS.');
      return;
    }
    setBusy(true);
    try {
      const credential = await confirmation.current!.confirm(code);
      verifiedUser.current = credential.user;
      setStep('password');
      setStatus({ text: 'Your number is verified. Choose a new password.', state: 'success' });
    } catch (error) {
      fail(firebaseMessage(error, 'We could not verify that code. Try again.'));
    } finally {
      setBusy(false);
    }
  }

  async function setNewPassword() {
    const element = form.current;
    const user = verifiedUser.current;
    if (!element || !user || !element.reportValidity()) return;
    const newPassword = fieldValue(element, 'newPassword');
    if (newPassword !== fieldValue(element, 'confirmPassword')) {
      fail('The two passwords do not match.');
      return;
    }
    setBusy(true);
    setStatus({ text: 'Saving your new password…', state: '' });
    try {
      const response = await postJson(
        `${shareApiBase()}${COMPLETE_PATH}`,
        {
          username: fieldValue(element, 'username').trim(),
          firebaseIdToken: await user.getIdToken(),
          newPassword,
        },
        COMPLETE_TIMEOUT_MS,
      );
      const body = (await response.json().catch(() => ({}))) as ServerError & {
        username?: string;
      };
      if (response.status === 401) {
        // The verification is too old for the server: back to a fresh code.
        await resetVerification();
        throw new Error(body.error || 'Please verify your mobile number again.');
      }
      if (!response.ok) {
        throw new Error(body.error || 'We could not reset the password. Please try again.');
      }
      setUsername(body.username || fieldValue(element, 'username').trim());
      setStep('done');
      setStatus({ text: '', state: '' });
      track('password_reset_submit');
      void signOutPhone();
    } catch (error) {
      if (error instanceof RequestTimeoutError) {
        fail(
          'The server took too long to answer. Your password may still have changed — try signing in with the new one before you try again.',
        );
      } else {
        fail(error instanceof Error ? error.message : 'We could not reset the password.');
      }
    } finally {
      setBusy(false);
    }
  }

  async function resetVerification() {
    confirmation.current = null;
    verifiedUser.current = null;
    verifier.current?.clear();
    verifier.current = null;
    setStep('details');
    await signOutPhone();
  }

  function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    if (step === 'details') void checkAccountAndSendCode();
    else if (step === 'code') void verifyCode();
    else if (step === 'password') void setNewPassword();
  }

  if (step === 'done') {
    return (
      <div className="card trial-done" role="status">
        <div className="eyebrow">Password changed</div>
        <h2>You can sign in again.</h2>
        <p>
          Use the username <strong>{username}</strong> and your new password on any Transport Book
          app. Every device that was still signed in with the old password has been signed out.
        </p>
        <div className="actions">
          <a
            className="button primary"
            href={webApp.url}
            rel="noopener"
            onClick={() => track('open_web_app', { placement: 'password_reset_done' })}
          >
            Open in the browser <span aria-hidden="true">↗</span>
          </a>
          <Link className="button secondary" to="/support">
            Support
          </Link>
        </div>
      </div>
    );
  }

  const detailsLocked = step !== 'details';

  return (
    <form
      ref={form}
      className="card demo-form trial-form"
      onSubmit={submit}
      onFocus={() => {
        if (started.current) return;
        started.current = true;
        track('password_reset_start');
      }}
      noValidate
    >
      <fieldset disabled={detailsLocked || busy}>
        <legend>Your account</legend>
        <div className="form-grid">
          <div className="field full">
            <label htmlFor="username">Username</label>
            <input
              id="username"
              name="username"
              required
              maxLength={120}
              autoComplete="username"
              autoCapitalize="none"
            />
            <small>
              The one you sign in with. Your email address works too, if it is on the account.
            </small>
          </div>
          <div className="field full">
            <label htmlFor="mobile">Mobile number on the account</label>
            <div className="phone-input">
              <span aria-hidden="true">+91</span>
              <input
                id="mobile"
                name="mobile"
                type="tel"
                inputMode="numeric"
                required
                pattern="[6-9][0-9]{9}"
                title="A 10-digit Indian mobile number"
                autoComplete="tel-national"
                maxLength={10}
              />
            </div>
            <small>We send a one-time code by SMS to this number.</small>
          </div>
        </div>
      </fieldset>

      {step === 'code' && (
        <div className="field trial-code">
          <label htmlFor="code">Verification code</label>
          <input
            id="code"
            name="code"
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]{6}"
            maxLength={6}
            required
          />
          <button
            type="button"
            className="link-button"
            disabled={busy}
            onClick={() => void resetVerification()}
          >
            Change the details or send a new code
          </button>
        </div>
      )}

      {step === 'password' && (
        <fieldset disabled={busy}>
          <legend>New password</legend>
          <div className="form-grid">
            <div className="field">
              <label htmlFor="newPassword">New password</label>
              <input
                id="newPassword"
                name="newPassword"
                type="password"
                required
                minLength={MIN_PASSWORD_LENGTH}
                maxLength={120}
                autoComplete="new-password"
              />
              <small>At least {MIN_PASSWORD_LENGTH} characters.</small>
            </div>
            <div className="field">
              <label htmlFor="confirmPassword">Repeat it</label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                required
                minLength={MIN_PASSWORD_LENGTH}
                maxLength={120}
                autoComplete="new-password"
              />
            </div>
          </div>
        </fieldset>
      )}

      <button className="button primary" type="submit" disabled={busy}>
        {step === 'details'
          ? 'Send verification code'
          : step === 'code'
            ? 'Verify the code'
            : 'Set my new password'}
      </button>
      <div id="reset-recaptcha" />
      <div className="form-status" data-state={status.state} role="status" aria-live="polite">
        {status.text}
      </div>
      <small className="trial-note">
        Remembered it after all? <a href={webApp.url}>Sign in</a>, or{' '}
        <Link to="/start-trial">start a free trial</Link> if you do not have an account yet.
      </small>
    </form>
  );
}
